import React, { useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useQuery } from "@apollo/client/react"
import { ConditionNodeData, WorkflowNode } from "lib/types/workflow"
import { Common } from "lib/types"
import {
  GetPaymentProvidersDocument,
  GetPaymentProvidersQuery,
  OrderWorkflowNodeConditionCheckType as CheckType,
  OrderWorkflowNodeConditionComparisonType as Comparison
} from "lib/types/generated/graphql"
import { getPaymentProviderDisplayName } from "helpers/getPaymentDisplayName"

import Input from "components/Ui/Form/Input"
import MultiSelect from "components/Ui/Form/MultiSelect"
import {
  BILLING_ADDRESS_EMAIL,
  PAYMENT_PROVIDER_NAME,
  ORDER_LINE_PRODUCT_PARENT_ID,
  ORDER_LINE_PRODUCT_VARIANT_ID
} from "helpers/workflowConverter"
import {
  AddValueButton,
  FormLabel,
  FormSection,
  HelperText,
  RemoveValueButton,
  SaveButton,
  SelectWrapper,
  TextArea,
  ValueListRow
} from "./NodeEditorSidebar.styled"

type Props = {
  node: WorkflowNode
  onSave: (nodeId: string, data: ConditionNodeData) => void
}

interface FormValues {
  label: string
  checkType: string
  comparison: string
  value: string
  endpoint: string
  customData: string
  billingEmailsText: string
}

const CHECK_TYPE_GROUPS: {
  label: string
  options: { value: string; label: string }[]
}[] = [
  {
    label: "Provider checks",
    options: [{ value: PAYMENT_PROVIDER_NAME, label: "Payment Provider" }]
  },
  {
    label: "Order line checks",
    options: [
      { value: ORDER_LINE_PRODUCT_PARENT_ID, label: "Product Parent ID" },
      { value: ORDER_LINE_PRODUCT_VARIANT_ID, label: "Product Variant ID" }
    ]
  },
  {
    label: "Order-level checks",
    options: [{ value: BILLING_ADDRESS_EMAIL, label: "Billing Address — Email list" }]
  },
  {
    label: "Custom",
    options: [{ value: CheckType.CustomHttp, label: "Custom HTTP Request" }]
  }
]

const parseEmailList = (raw: string): string[] => {
  return Array.from(
    new Set(
      raw
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    )
  )
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const findInvalidEmails = (emails: string[]): string[] => emails.filter((e) => !EMAIL_RE.test(e))

const MAX_EMAILS = 100

const COMPARISONS = [
  { value: Comparison.Equals, label: "Equals" },
  { value: Comparison.NotEquals, label: "Not Equals" },
  { value: Comparison.Contains, label: "Contains" },
  { value: Comparison.NotContains, label: "Not Contains" },
  { value: Comparison.BeginsWith, label: "Begins With" },
  { value: Comparison.EndsWith, label: "Ends With" }
]

const EQUALITY_ONLY_CHECK_TYPES = new Set<string>([PAYMENT_PROVIDER_NAME])

const getComparisonsForCheckType = (checkType: string) =>
  EQUALITY_ONLY_CHECK_TYPES.has(checkType)
    ? COMPARISONS.filter((c) => c.value === Comparison.Equals || c.value === Comparison.NotEquals)
    : COMPARISONS

const isValidJson = (value: string): boolean => {
  if (!value.trim()) return true
  try {
    JSON.parse(value)
    return true
  } catch {
    return false
  }
}

const ConditionNodeForm = ({ node, onSave }: Props) => {
  const data = node.data as ConditionNodeData

  const initialCheckType = data.checkType || PAYMENT_PROVIDER_NAME

  const { data: providersData, loading: loadingProviders } = useQuery<GetPaymentProvidersQuery>(
    GetPaymentProvidersDocument,
    {
      skip: initialCheckType !== PAYMENT_PROVIDER_NAME
    }
  )

  const providerOptions: Common.Option[] = useMemo(() => {
    const providers = providersData?.getPaymentProviders ?? []
    const seen = new Set<string>()
    const options: Common.Option[] = []
    for (const p of providers) {
      if (!seen.has(p.id)) {
        seen.add(p.id)
        options.push({
          value: p.id,
          label: getPaymentProviderDisplayName(p.id)
        })
      }
    }
    return options
  }, [providersData])

  const [selectedProviders, setSelectedProviders] = useState<Common.Option[]>(() =>
    data.checkType === PAYMENT_PROVIDER_NAME
      ? (data.values ?? []).map((v) => ({
          value: v,
          label: getPaymentProviderDisplayName(v)
        }))
      : []
  )

  const [multiValues, setMultiValues] = useState<string[]>(() => {
    const comparison = data.comparison || Comparison.Equals
    if (comparison === Comparison.Contains || comparison === Comparison.NotContains) {
      return data.values?.length ? [...data.values] : [""]
    }
    return [""]
  })

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      label: data.label,
      checkType: data.checkType || PAYMENT_PROVIDER_NAME,
      comparison: data.comparison || Comparison.Equals,
      value:
        data.checkType !== PAYMENT_PROVIDER_NAME &&
        data.checkType !== CheckType.CustomHttp &&
        data.checkType !== BILLING_ADDRESS_EMAIL
          ? (data.values?.[0] ?? "")
          : "",
      endpoint: data.endpoint ?? "",
      customData: data.customData ?? "",
      billingEmailsText:
        data.checkType === BILLING_ADDRESS_EMAIL ? (data.values ?? []).join("\n") : ""
    }
  })

  const watchCheckType = watch("checkType")
  const watchComparison = watch("comparison")
  const comparisonOptions = useMemo(
    () => getComparisonsForCheckType(watchCheckType),
    [watchCheckType]
  )

  useEffect(() => {
    if (!comparisonOptions.some((c) => c.value === watchComparison)) {
      setValue("comparison", Comparison.Equals)
    }
  }, [comparisonOptions, watchComparison, setValue])
  const watchBillingEmailsText = watch("billingEmailsText")
  const isPaymentProvider = watchCheckType === PAYMENT_PROVIDER_NAME
  const isCustomHttp = watchCheckType === CheckType.CustomHttp
  const isBillingEmail = watchCheckType === BILLING_ADDRESS_EMAIL
  const isMultiValue =
    watchComparison === Comparison.Contains || watchComparison === Comparison.NotContains

  const parsedBillingEmails = useMemo(
    () => (isBillingEmail ? parseEmailList(watchBillingEmailsText ?? "") : []),
    [isBillingEmail, watchBillingEmailsText]
  )
  const invalidBillingEmails = useMemo(
    () => findInvalidEmails(parsedBillingEmails),
    [parsedBillingEmails]
  )

  const onSubmit = (values: FormValues) => {
    if (isCustomHttp) {
      onSave(node.id, {
        nodeType: "CONDITION",
        label: values.label,
        checkType: CheckType.CustomHttp,
        comparison: "",
        values: [],
        endpoint: values.endpoint,
        customData: values.customData || undefined
      })
      return
    }

    if (isBillingEmail) {
      if (
        parsedBillingEmails.length === 0 ||
        parsedBillingEmails.length > MAX_EMAILS ||
        invalidBillingEmails.length > 0
      )
        return

      onSave(node.id, {
        nodeType: "CONDITION",
        label: values.label,
        checkType: BILLING_ADDRESS_EMAIL,
        comparison: Comparison.Equals,
        values: parsedBillingEmails
      })
      return
    }

    if (isMultiValue && multiValues.filter((v) => v.trim()).length === 0) return

    const conditionValues = isPaymentProvider
      ? selectedProviders.map((p) => p.value)
      : isMultiValue
        ? multiValues.filter((v) => v.trim() !== "")
        : values.value
          ? [values.value]
          : []

    onSave(node.id, {
      nodeType: "CONDITION",
      label: values.label,
      checkType: values.checkType,
      comparison: values.comparison,
      values: conditionValues
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormSection>
        <Input
          name="label"
          label="Name"
          register={register}
          validation={{
            required: "Name is required",
            maxLength: { value: 100, message: "Name must be at most 100 characters" }
          }}
          errors={errors}
        />
      </FormSection>
      <SelectWrapper>
        <FormLabel>Check Type</FormLabel>
        <select {...register("checkType")}>
          {CHECK_TYPE_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map((ct) => (
                <option key={ct.value} value={ct.value}>
                  {ct.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </SelectWrapper>

      {isBillingEmail ? (
        <>
          <FormSection>
            <FormLabel>Billing address emails (max {MAX_EMAILS})</FormLabel>
            <TextArea
              {...register("billingEmailsText")}
              placeholder={"alice@example.com\nbob@example.com\n..."}
              rows={10}
            />
          </FormSection>
          <HelperText>
            <strong>
              {parsedBillingEmails.length} unique email
              {parsedBillingEmails.length === 1 ? "" : "s"}
            </strong>
            Paste or type one email per line (commas, semicolons, and spaces also work). The
            workflow matches if the order&rsquo;s billing address email equals any of the listed
            values.
            {parsedBillingEmails.length > MAX_EMAILS && (
              <>
                <br />
                <span style={{ color: "#cc3333" }}>
                  {parsedBillingEmails.length} emails exceeds the max of {MAX_EMAILS}. Remove{" "}
                  {parsedBillingEmails.length - MAX_EMAILS} to save.
                </span>
              </>
            )}
            {invalidBillingEmails.length > 0 && (
              <>
                <br />
                <span style={{ color: "#cc3333" }}>
                  Invalid format: {invalidBillingEmails.slice(0, 3).join(", ")}
                  {invalidBillingEmails.length > 3
                    ? ` and ${invalidBillingEmails.length - 3} more`
                    : ""}
                </span>
              </>
            )}
          </HelperText>
        </>
      ) : isCustomHttp ? (
        <>
          <FormSection>
            <Input
              name="endpoint"
              label="Endpoint URL"
              register={register}
              validation={{
                required: "Endpoint URL is required",
                pattern: {
                  value: /^https:\/\/.+/,
                  message: "Must be a valid HTTPS URL"
                }
              }}
              errors={errors}
            />
          </FormSection>
          <FormSection>
            <FormLabel>Request Body (JSON)</FormLabel>
            <TextArea
              {...register("customData", {
                validate: (v) => isValidJson(v) || "Must be valid JSON"
              })}
              placeholder={'{\n  "key": "value"\n}'}
            />
            {errors.customData && (
              <span style={{ color: "red", fontSize: "1.1rem" }}>{errors.customData.message}</span>
            )}
          </FormSection>
          <HelperText>
            <strong>Your endpoint receives a POST with:</strong>
            <pre>{`{
  "workflowId": "1fca8ad7-0905-...",
  "workflowRevision": 0,
  "executionId": "ce24cc5c-b30d-...",
  "orderId": "cca69007-82f8-...",
  "customData": { <your JSON above> }
}`}</pre>
            <strong>Expected response:</strong>
            Return <code>{`{ "match": true }`}</code> → <strong>Yes</strong> branch, or{" "}
            <code>{`{ "match": false }`}</code> → <strong>No</strong> branch. Optionally include{" "}
            <code>&quot;errorMessage&quot;</code> for debugging.
          </HelperText>
        </>
      ) : (
        <>
          <SelectWrapper>
            <FormLabel>Comparison</FormLabel>
            <select {...register("comparison")}>
              {comparisonOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </SelectWrapper>

          {isPaymentProvider ? (
            <FormSection>
              <MultiSelect
                label="Payment Providers"
                options={providerOptions}
                value={selectedProviders}
                setValue={setSelectedProviders}
                placeholder="Select providers..."
                loading={loadingProviders}
              />
            </FormSection>
          ) : isMultiValue ? (
            <FormSection>
              <FormLabel>Values (max 10)</FormLabel>
              {multiValues.map((val, idx) => (
                <ValueListRow key={idx}>
                  <input
                    type="text"
                    value={val}
                    maxLength={100}
                    placeholder={`Value ${idx + 1}`}
                    onChange={(e) => {
                      const updated = [...multiValues]
                      updated[idx] = e.target.value
                      setMultiValues(updated)
                    }}
                  />
                  {multiValues.length > 1 && (
                    <RemoveValueButton
                      type="button"
                      onClick={() => setMultiValues(multiValues.filter((_, i) => i !== idx))}
                    >
                      ×
                    </RemoveValueButton>
                  )}
                </ValueListRow>
              ))}
              <AddValueButton
                type="button"
                disabled={multiValues.length >= 10}
                onClick={() => setMultiValues([...multiValues, ""])}
              >
                + Add value
              </AddValueButton>
            </FormSection>
          ) : (
            <FormSection>
              <Input
                name="value"
                label="Value"
                register={register}
                validation={{
                  required: "Value is required",
                  maxLength: { value: 100, message: "Max 100 characters" }
                }}
                errors={errors}
              />
            </FormSection>
          )}
        </>
      )}

      <SaveButton type="submit">Update Node</SaveButton>
    </form>
  )
}

export default ConditionNodeForm
